import { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  Box,
  TextField,
  RadioGroup,
  Radio,
  FormControl,
  Button,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import TicketConfirmations from "./TicketConfirmation";

// List of all international phone country codes
const COUNTRY_CODES = [
  "+1",
  "+7",
  "+20",
  "+27",
  "+30",
  "+31",
  "+32",
  "+33",
  "+34",
  "+36",
  "+39",
  "+40",
  "+41",
  "+43",
  "+44",
  "+45",
  "+46",
  "+47",
  "+48",
  "+49",
  "+51",
  "+52",
  "+53",
  "+54",
  "+55",
  "+56",
  "+57",
  "+58",
  "+60",
  "+61",
  "+62",
  "+63",
  "+64",
  "+65",
  "+66",
  "+81",
  "+82",
  "+84",
  "+86",
  "+90",
  "+91",
  "+92",
  "+93",
  "+94",
  "+95",
  "+98",
  "+211",
  "+212",
  "+213",
  "+216",
  "+218",
  "+220",
  "+221",
  "+222",
  "+223",
  "+224",
  "+225",
  "+226",
  "+227",
  "+228",
  "+229",
  "+230",
  "+231",
  "+232",
  "+233",
  "+234",
  "+235",
  "+236",
  "+237",
  "+238",
  "+239",
  "+240",
  "+241",
  "+242",
  "+243",
  "+244",
  "+245",
  "+246",
  "+248",
  "+249",
  "+250",
  "+251",
  "+252",
  "+253",
  "+254",
  "+255",
  "+256",
  "+257",
  "+258",
  "+260",
  "+261",
  "+262",
  "+263",
  "+264",
  "+265",
  "+266",
  "+267",
  "+268",
  "+269",
  "+350",
  "+351",
  "+352",
  "+353",
  "+354",
  "+355",
  "+356",
  "+357",
  "+358",
  "+359",
  "+370",
  "+371",
  "+372",
  "+373",
  "+374",
  "+375",
  "+376",
  "+377",
  "+378",
  "+379",
  "+380",
  "+381",
  "+382",
  "+383",
  "+385",
  "+386",
  "+387",
  "+389",
  "+420",
  "+421",
  "+423",
  "+500",
  "+501",
  "+502",
  "+503",
  "+504",
  "+505",
  "+506",
  "+507",
  "+508",
  "+509",
  "+590",
  "+591",
  "+592",
  "+593",
  "+594",
  "+595",
  "+596",
  "+597",
  "+598",
  "+599",
  "+670",
  "+672",
  "+673",
  "+674",
  "+675",
  "+676",
  "+677",
  "+678",
  "+679",
  "+680",
  "+681",
  "+682",
  "+683",
  "+685",
  "+686",
  "+687",
  "+688",
  "+689",
  "+690",
  "+691",
  "+692",
  "+850",
  "+852",
  "+853",
  "+855",
  "+856",
  "+870",
  "+880",
  "+886",
  "+960",
  "+961",
  "+962",
  "+963",
  "+964",
  "+965",
  "+966",
  "+967",
  "+968",
  "+970",
  "+971",
  "+972",
  "+973",
  "+974",
  "+975",
  "+976",
  "+977",
  "+992",
  "+993",
  "+994",
  "+995",
  "+996",
  "+998",
];

const OrgConfig = ({ config, onChange }) => {
  const [settings, setSettings] = useState({
    photoRequired: false,
    showFeedback: false,
    showFeedbackAfter: 48,
    receiveFeedbackNotification: false,
    autoCompleteEvents: false,
    autoCompleteMode: "minutes",
    autoCompleteAfter: 120,
    autoCompleteTime: "23:00",
    autoSendMatches: false,
    defaultLanguage: "en",
    defaultCountryCode: "+1",
    checkGender: true,
    waiverUrl: "",
    ...(config || {}),
  });
  const [ticketConfig, setTicketConfig] = useState(null);

  useEffect(() => {
    console.log(settings);
  },[settings])


  useEffect(() => {
    if (config) {
      setSettings((prev) => ({ ...prev, ...config }));
      setTicketConfig(config?.ticketing);
    }
  }, [config]);

  const update = (field, value) => {
    setSettings((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "showFeedback" && !value) {
        updated.receiveFeedbackNotification = false;
      }
      if (onChange) onChange(updated);
      return updated;
    });
  };

  const AutoCompleteSection = () => {
    if (!settings.autoCompleteEvents) return null;
    return (
      <Box
        sx={{
          border: "1px solid #eee",
          borderRadius: 1,
          p: 2,
          mb: 2,
          background: "#fafafa",
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Auto Complete Settings
        </Typography>
        <FormControl component="fieldset" fullWidth sx={{ mb: 1 }}>
          <RadioGroup
            row
            value={settings.autoCompleteMode}
            onChange={(e) => update("autoCompleteMode", e.target.value)}
          >
            <FormControlLabel
              value="minutes"
              control={<Radio size="small" />}
              label="After minutes"
            />
            <FormControlLabel
              value="time"
              control={<Radio size="small" />}
              label="At specific time"
            />
          </RadioGroup>
        </FormControl>
        {settings.autoCompleteMode === "minutes" ? (
          <TextField
            type="number"
            inputProps={{
              min: 1,
              step: 1,
              pattern: "[0-9]*",
              inputMode: "numeric",
            }}
            label="Auto Complete After (minutes)"
            value={settings.autoCompleteAfter}
            onChange={(e) => {
              const val = e.target.value;
              if (/^\d*$/.test(val)) {
                update(
                  "autoCompleteAfter",
                  val === "" ? "" : parseInt(val, 10)
                );
              }
            }}
            variant="outlined"
            size="small"
            sx={{
              mb: 1.5,
              mt: 0.5,
              maxWidth: 220,
              "& .MuiInputBase-root": { background: "#f9fafc" },
            }}
            fullWidth
            placeholder="e.g. 120"
            helperText="Event completes X minutes after last round starts"
          />
        ) : (
          <TextField
            type="time"
            label="Complete At"
            value={settings.autoCompleteTime}
            onChange={(e) => update("autoCompleteTime", e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mb: 1.5, mt: 0.5, maxWidth: 220 }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="Clock time to complete events"
          />
        )}
        <FormControlLabel
          control={
            <Switch
              checked={settings.autoSendMatches}
              onChange={() =>
                update("autoSendMatches", !settings.autoSendMatches)
              }
            />
          }
          label="Auto Send Matches"
        />
        <Typography variant="caption" sx={{ pl: 1, display: "block" }}>
          When enabled, matches automatically email to attendees after
          completion.
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Paper sx={{ p: 3 }} elevation={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              General Configuration
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl variant="outlined" sx={{ mr: 2, minWidth: 120 }}>
              <InputLabel id="default-language-label">
                Default Language
              </InputLabel>
              <Select
                labelId="default-language-label"
                label="Default Language"
                value={settings.defaultLanguage}
                onChange={(e) => update("defaultLanguage", e.target.value)}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="es">Spanish</MenuItem>
                <MenuItem value="gb">English (UK)</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant="outlined" sx={{ minWidth: 120 }}>
              <InputLabel id="default-country-code-label">
                Country Code
              </InputLabel>
              <Select
                labelId="default-country-code-label"
                label="Country Code"
                value={settings.defaultCountryCode}
                onChange={(e) => update("defaultCountryCode", e.target.value)}
              >
                {COUNTRY_CODES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Default Event Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These defaults apply when creating new events. Individual events
              can override them.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.checkGender}
                    onChange={() =>
                      update("checkGender", !settings.checkGender)
                    }
                  />
                }
                label="Check Gender"
              />
              <FormHelperText style={{ marginLeft: 24, marginBottom: 10 }}>
                When enabled, the system will attempt to determine the
                purchaser’s gender based on their first name and compare it to
                the ticket type they selected. If a mismatch is detected, you
                will receive a notification.
              </FormHelperText>{" "}
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.photoRequired}
                    onChange={() =>
                      update("photoRequired", !settings.photoRequired)
                    }
                  />
                }
                label="Require Profile Picture"
              />
              <FormHelperText style={{ marginLeft: 24, marginBottom: 10 }}>
                When enabled, attendees must have a profile picture added to
                their profile in order to check in
              </FormHelperText>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showFeedback}
                    onChange={() =>
                      update("showFeedback", !settings.showFeedback)
                    }
                  />
                }
                label="Show Feedback"
              />
              <FormHelperText style={{ marginLeft: 24, marginBottom: 10 }}>
                When enabled, attendees will be able to leave feedback on the
                matches they received at an event.
              </FormHelperText>
              {settings.showFeedback && (
                <>
                  <TextField
                    type="number"
                    inputProps={{
                      min: 0,
                      step: 1,
                      pattern: "[0-9]*",
                      inputMode: "numeric",
                    }}
                    label="Allow feedback after (hours)"
                    value={settings.showFeedbackAfter}
                    style={{ marginTop: 10 }}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*$/.test(val)) {
                        update(
                          "showFeedbackAfter",
                          val === "" ? "" : parseInt(val, 10)
                        );
                      }
                    }}
                    variant="outlined"
                    size="small"
                    sx={{
                      ml: 3,
                      mb: 1,
                      maxWidth: 220,
                      "& .MuiInputBase-root": { background: "#f9fafc" },
                    }}
                  />
                  <FormHelperText style={{ marginLeft: 24 }}>
                    Feedback dropdown appears X hours after event ends
                  </FormHelperText>
                  <FormControlLabel
                    sx={{ ml: 3 }}
                    control={
                      <Switch
                        checked={settings.receiveFeedbackNotification}
                        onChange={() =>
                          update(
                            "receiveFeedbackNotification",
                            !settings.receiveFeedbackNotification
                          )
                        }
                      />
                    }
                    label="Receive Feedback Notifications"
                  />
                </>
              )}
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={settings.autoCompleteEvents}
                    onChange={() =>
                      update("autoCompleteEvents", !settings.autoCompleteEvents)
                    }
                  />
                }
                label="Auto Complete Events"
              />
              <FormHelperText style={{ marginLeft: 24, marginBottom: 10 }}>
                When enabled, event will complete automatically based on a
                schedule you set
              </FormHelperText>
            </FormGroup>
            <AutoCompleteSection />
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ mt: 2, p: 3 }} elevation={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Notifications
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.imbalanceRatio?.receiveNotifications || false}
                    onChange={() => {
                      const current =
                        settings.imbalanceRatio || {
                          receiveNotifications: false,
                          threshold: 5,
                          timeWindow: 14,
                        };
                      update("imbalanceRatio", {
                        ...current,
                        receiveNotifications: !current.receiveNotifications,
                      });
                    }}
                  />
                }
                label="Ticket Imbalance Notifications"
              />
              <FormHelperText style={{ marginLeft: 24, marginBottom: 10 }}>
                When enabled, you will receive notifications if ticket sales for an event
                become imbalanced between men and women.
              </FormHelperText>

              {settings.imbalanceRatio?.receiveNotifications && (
                <>
                  <TextField
                    type="number"
                    inputProps={{
                      min: 1,
                      step: 1,
                      pattern: "[0-9]*",
                      inputMode: "numeric",
                    }}
                    label="Imbalance Threshold"
                    value={
                      settings.imbalanceRatio?.threshold === undefined
                        ? ""
                        : settings.imbalanceRatio.threshold
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!/^\d*$/.test(val)) return;
                      const current =
                        settings.imbalanceRatio || {
                          receiveNotifications: true,
                          threshold: 5,
                          timeWindow: 14,
                        };
                      if (val === "") {
                        update("imbalanceRatio", { ...current, threshold: "" });
                      } else {
                        update("imbalanceRatio", {
                          ...current,
                          threshold: parseInt(val, 10),
                        });
                      }
                    }}
                    variant="outlined"
                    size="small"
                    sx={{
                      ml: 3,
                      mb: 1,
                      maxWidth: 220,
                      "& .MuiInputBase-root": { background: "#f9fafc" },
                    }}
                    helperText="Maximum allowed difference in ticket count before triggering an alert (positive integer)."
                  />

                  <TextField
                    type="number"
                    inputProps={{
                      min: 1,
                      step: 1,
                      pattern: "[0-9]*",
                      inputMode: "numeric",
                    }}
                    label="Time Window (days)"
                    value={
                      settings.imbalanceRatio?.timeWindow === undefined
                        ? ""
                        : settings.imbalanceRatio.timeWindow
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!/^\d*$/.test(val)) return;
                      const current =
                        settings.imbalanceRatio || {
                          receiveNotifications: true,
                          threshold: 5,
                          timeWindow: 14,
                        };
                      if (val === "") {
                        update("imbalanceRatio", { ...current, timeWindow: "" });
                      } else {
                        update("imbalanceRatio", {
                          ...current,
                          timeWindow: parseInt(val, 10),
                        });
                      }
                    }}
                    variant="outlined"
                    size="small"
                    sx={{
                      ml: 3,
                      mb: 1,
                      maxWidth: 220,
                      "& .MuiInputBase-root": { background: "#f9fafc" },
                    }}
                    helperText="Number of days to look ahead for upcoming events when checking imbalance (positive integer)."
                  />
                </>
              )}
            </FormGroup>
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ mt: 2, p: 3 }} elevation={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Sports Configuration
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Waiver Base URL"
              value={settings.waiverUrl || ""}
              onChange={(e) => update("waiverUrl", e.target.value)}
              placeholder="https://waivers.thenextfunthing.com"
              size="small"
            />
            <FormHelperText>
              Base URL used for public waiver signing links.
            </FormHelperText>
          </Grid>
        </Grid>
      </Paper>
      <TicketConfirmations
        config={ticketConfig}
        org={config?.org}
        configId={config?._id}
      />
    </>
  );
};

export default OrgConfig;
